// * relative
import _ from "lodash";
import { connect } from "react-redux";
import React, { useRef, useMemo, useCallback, useEffect } from "react";
import classnames from "classnames";

// * internal
import { getIsFromClassroom } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { createAIConversation } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import {
  TODDLE_LIBRARY,
  WORKSHEET_QUESTION_CREATION,
} from "Constants/stringConstants";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import {
  FIELD_UID_ADD_LEARNING_GOALS,
  WORKSHEET_ELEMENT_IDS,
} from "Worksheet/modules/WorksheetConstants";
import { getAddLearningGoalsDropdownOptions } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import {
  updateAddQuestionsInBetween,
  updateAIAssessmentTools,
  updateLoadingState,
  updateVisibleQuestionAssistantIds,
  updateWorksheetSidebar,
} from "Worksheet/modules/WorksheetModules";
import {
  getOptionsFromField,
  getFormattedQuestion,
  getLearningGoalsFields,
  getAssessmentDetailsData,
} from "Worksheet/modules/WorksheetUtils";
import {
  getDynamicTemplate,
  updateAssessmentTemplate,
} from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/Assessments/modules/Utils";
import {
  getUserType,
  getActiveTab,
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

// * relative
import classes from "./SinglePageQuestionEditorV2.scss";
import InstructionAndToolsController from "./InstructionAndToolsController";
import AddQuestionDropdownWrapper from "./AddQuestionDropdownWrapper";
import AssistantAndToolWrapper from "./AssistantAndToolWrapper";
import WorksheetBasicDetails from "./WorksheetBasicDetails";
import QuestionPane from "./QuestionPane";
import {
  getActiveQuestionV2,
  getLearningGoalsFieldsData,
  getQuestionInfoForAiAssistantV2,
} from "./SinglePageQuestionEditorUtils";

/**
 *
 *
 *
 * @param {*} params
 * @returns It renders question in single list as single page UI and we maintain the active question
 */
const SinglePageQuestionEditorV2 = ({
  t,
  mode,
  setQuestionRef,
  showWorksheetAssistant,
  classSubjects,
  parentId,
  portalType,
  creationMode,
  unitPlanTemplateId,
  getLearningGoalsMappingWrapper,
  worksheetDetailsRef,
  courseId,
  selectedCourse,
  unitPlanId,
  isFromUnitPlan,
  isDraft,
  sourceFrom,
  sections,
  activeQuestionId,
  createAIConversation,
  updateVisibleQuestionAssistantIds,
  learningGoalsDropdownOptions,
  selectedQuestionFields,
  updateLoadingState,
  questionAIConversationId,
  getAddButton,
  isGeneratingAIQuestions,
  updateWorksheetSidebar,
  assessmentDetailsComponent,
  updateAddQuestionsInBetween,
  hasAssessmentTools,
  hasInstruction,
  isAIAssessmentToolsOpen,
  assessmentToolConversationId,
  assessmentTools,
  assessmentDetails,
  getAssessmentRefetch,
  updateAIAssessmentTools,
}) => {
  const containerRef = useRef(null);
  const spaceContainerInBetweenRef = useRef(null);
  const hasSections = !_.isEmpty(sections);
  const [
    worksheetBasicDetailInfo,
    setWorksheetBasicDetailsInfo,
  ] = React.useState({
    showInstructions: false,
    showAssessmentTools: false,
  });

  useEffect(() => {
    if (activeQuestionId === WORKSHEET_QUESTION_CREATION) {
      // * Scroll to the top when we're on the question creation view
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  }, [activeQuestionId]);

  // * Getting the active question details for the question assistant
  const questionInfoForAssistant = useMemo(
    () => getQuestionInfoForAiAssistantV2(t, activeQuestionId, sections),
    [t, activeQuestionId, sections]
  );

  // * Handling the show/hide of question assistant and create ai conversation
  const handleShowQuestionAssistant = () => {
    // * Closing the question assistant if already showing or not an inactive question
    if (showWorksheetAssistant || !activeQuestionId) {
      updateVisibleQuestionAssistantIds([]);
      // toggling sidebar on smaller screens for better visibility when AiQuestionAssistant is opened
      if (window.innerWidth < 1150) {
        updateWorksheetSidebar({ isOpen: true });
      }
      return;
    }

    // * Finding Question assistant AI Conversation ID
    const { questionAIConversationId } = getActiveQuestionV2(
      sections,
      activeQuestionId
    );

    // * Checking and creating AI conversation if not exists
    if (_.isEmpty(questionAIConversationId)) {
      // * Getting learning goals field data
      const learningGoalsFieldsData = getLearningGoalsFieldsData(
        selectedQuestionFields,
        learningGoalsDropdownOptions
      );

      // * Creating ai conversation
      createAIConversation({
        input: {
          entityType: "QUESTION",
          entityId: activeQuestionId,
          metadata: { allFields: learningGoalsFieldsData, localFields: [] },
        },
        isQuestionAssistant: true,
      });
    }

    // * Updating the visible question assistant ids
    updateVisibleQuestionAssistantIds([activeQuestionId]);
    // toggling sidebar on smaller screens for better visibility when AiQuestionAssistant is opened
    if (window.innerWidth < 1150) {
      updateWorksheetSidebar({ isOpen: false });
    }
  };

  // * Showing full screen loader controlled by assistant
  const showFullScreenLoader = useCallback(state => updateLoadingState(state), [
    updateLoadingState,
  ]);

  const newWorksheetBasicDetailManager = {
    worksheetBasicDetailInfo,
    setWorksheetBasicDetailsInfo,
  };

  const getInstructionAndToolsController = useCallback(
    () => (
      <InstructionAndToolsController
        t={t}
        hasInstruction={hasInstruction}
        hasAssessmentTools={hasAssessmentTools}
        worksheetBasicDetailInfo={worksheetBasicDetailInfo}
        setWorksheetBasicDetailsInfo={setWorksheetBasicDetailsInfo}
      />
    ),
    [
      t,
      worksheetBasicDetailInfo,
      setWorksheetBasicDetailsInfo,
      hasAssessmentTools,
      hasInstruction,
    ]
  );

  const handleCloseAIAssessmentTools = () => {
    updateAIAssessmentTools({ isOpen: false });
    // toggling sidebar on smaller screens for better visibility when AIAssesmentTools is closed
    if (window.innerWidth < 1192) {
      updateWorksheetSidebar({ isOpen: true });
    }
  };

  const showAddButton = !(
    isGeneratingAIQuestions ||
    isAIAssessmentToolsOpen ||
    showWorksheetAssistant
  );

  const singlePageQuestionEditorClasses = classnames({
    [classes.singlePageQuestionEditor]: true,
    [classes.singlePageQuestionEditorV2]: !isGeneratingAIQuestions,
    [classes.singlePageQuestionEditorV3]: true,
  });

  return (
    <>
      <div
        id={WORKSHEET_ELEMENT_IDS.WORKSHEET_SINGLE_PAGE_CONTAINER}
        ref={containerRef}
        className={classes.container}
      >
        <div className={singlePageQuestionEditorClasses}>
          {/* WORKSHEET BASIC DETAILS */}
          <WorksheetBasicDetails
            hasSections={hasSections}
            assessmentDetailsComponent={assessmentDetailsComponent}
            newWorksheetBasicDetailManager={newWorksheetBasicDetailManager}
            worksheetBasicDetailInfo={worksheetBasicDetailInfo}
            isGeneratingAIQuestions={isGeneratingAIQuestions}
            hasInstruction={hasInstruction}
            setWorksheetBasicDetailsInfo={setWorksheetBasicDetailsInfo}
            hasAssessmentTools={hasAssessmentTools}
            t={t}
          />

          {/* LIST OF SECTIONS AND QUESTIONS */}
          <QuestionPane
            sections={sections}
            mode={mode}
            t={t}
            isDraft={isDraft}
            courseId={courseId}
            portalType={portalType}
            sourceFrom={sourceFrom}
            parentId={parentId}
            unitPlanId={unitPlanId}
            creationMode={creationMode}
            classSubjects={classSubjects}
            setQuestionRef={setQuestionRef}
            selectedCourse={selectedCourse}
            isFromUnitPlan={isFromUnitPlan}
            unitPlanTemplateId={unitPlanTemplateId}
            worksheetDetailsRef={worksheetDetailsRef}
            showWorksheetAssistant={showWorksheetAssistant}
            getLearningGoalsMappingWrapper={getLearningGoalsMappingWrapper}
            activeQuestionId={activeQuestionId}
            isAIAssessmentToolsOpen={isAIAssessmentToolsOpen}
            isGeneratingAIQuestions={isGeneratingAIQuestions}
            getAddButton={getAddButton}
            spaceContainerInBetweenRef={spaceContainerInBetweenRef}
            updateAddQuestionsInBetween={updateAddQuestionsInBetween}
          />

          {/* BUTTON TO ADD QUESTIONS AND SECTION */}
          <AddQuestionDropdownWrapper
            t={t}
            hasSections={hasSections}
            showAddButton={showAddButton}
            getAddButton={getAddButton}
            activeQuestionId={activeQuestionId}
            isGeneratingAIQuestions={isGeneratingAIQuestions}
            containerRef={containerRef}
            spaceContainerInBetweenRef={spaceContainerInBetweenRef}
          />
        </div>
      </div>

      <AssistantAndToolWrapper
        showWorksheetAssistant={showWorksheetAssistant}
        questionAIConversationId={questionAIConversationId}
        questionInfoForAssistant={questionInfoForAssistant}
        parentId={parentId}
        isDraft={isDraft}
        courseId={courseId}
        activeQuestionId={activeQuestionId}
        unitPlanId={unitPlanId}
        sourceFrom={sourceFrom}
        portalType={portalType}
        creationMode={creationMode}
        selectedCourse={selectedCourse}
        isFromUnitPlan={isFromUnitPlan}
        worksheetDetailsRef={worksheetDetailsRef}
        handleShowQuestionAssistant={handleShowQuestionAssistant}
        showFullScreenLoader={showFullScreenLoader}
        isAIAssessmentToolsOpen={isAIAssessmentToolsOpen}
        assessmentTools={assessmentTools}
        assessmentDetails={assessmentDetails}
        handleCloseAIAssessmentTools={handleCloseAIAssessmentTools}
        getAssessmentRefetch={getAssessmentRefetch}
        assessmentToolConversationId={assessmentToolConversationId}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    mode,
    sections,
    sourceFrom,
    assessmentId,
    creationMode,
    classSubjects,
    unitPlanTemplateId,
  } = ownProps;

  const activeQuestionId = _.get(state, "worksheet.activeQuestion");

  const isGenWithAIEnabled = _.get(
    state,
    "worksheet.isAIQuestionGenerationEnabled",
    false
  );

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const visibleQuestionAssistantIds = _.get(
    state,
    "worksheet.visibleQuestionAssistantIds",
    []
  );

  const isAIAssessmentToolsOpen = _.get(
    state,
    "worksheet.aiAssessmentTools.isOpen",
    false
  );

  const showWorksheetAssistant = _.includes(
    visibleQuestionAssistantIds,
    activeQuestionId
  );

  // * Getting learning goals dropdown options
  const {
    questionAIConversationId,
    question: activeQuestion,
    questionIndex: activeQuestionIndex,
  } = getActiveQuestionV2(sections, activeQuestionId);

  const activeSelectedQuestion =
    !_.isEmpty(activeQuestion) &&
    getFormattedQuestion({
      mode,
      question: activeQuestion,
      questionIndex: activeQuestionIndex,
      subQuestions: _.get(activeQuestion, "children", []),
      t,
    });

  const selectedQuestionFields = _.get(
    activeSelectedQuestion,
    "questionFields",
    []
  );

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const {
    assessmentType,
    worksheetTemplateId,
    unitType,
    unitPlanField,
    subjectsField,
    assessmentFields,
    assessmentDetails,
  } = getAssessmentDetailsData({ assessmentId, collaboratorsFilter });

  // * Check if the assessment has instructions
  const description = _.find(assessmentFields, { uid: "description" });
  const hasInstruction = !_.isEmpty(_.get(description, "value"));

  // * Check if the assessment has assessment tools other than SCORE(this is default)
  const assessmentTools = _.filter(
    _.get(assessmentDetails, "assessmentTools.edges"),
    item =>
      _.get(item, "node.assessmentToolType") !== "SCORE" &&
      _.get(item, "node.assessmentToolType") !== "COMMENT"
  );

  const hasAssessmentTools = !_.isEmpty(assessmentTools);

  const worksheetTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [worksheetTemplateId] },
    false
  );

  const unitPlanTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [unitPlanTemplateId] },
    false
  );

  const worksheetTemplate = _.get(worksheetTemplateCacheData, "body", {});
  const unitPlanTemplate = _.get(unitPlanTemplateCacheData, "body", {});

  const isFromClassroom = getIsFromClassroom({
    sourceFrom,
    template: worksheetTemplate,
  });

  const isToddleLibrary = getActiveTab(state) == TODDLE_LIBRARY;

  const curriculumType = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram.type", "")
    : getCurrentCurriculumProgramType(state);

  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );

  const originalTemplate = getDynamicTemplate({
    template: worksheetTemplate,
    unitType,
    t,
    curriculumType,
    plannerElementSets,
    subjects: classSubjects,
    creationMode,
    curriculumId: getSelectedCurriculumId(state),
    templateId: worksheetTemplateId,
  });

  /** unit plan & subjects field needed for show unit/subjects based data in learning goals */
  const resourceFields = [
    ...selectedQuestionFields,
    unitPlanField,
    subjectsField,
  ];

  const updatedTemplate = updateAssessmentTemplate({
    t,
    template: originalTemplate,
    assessmentType,
    isFromClassroom,
    curriculumType,
    resourceFields,
    mode: "edit",
    templateId: worksheetTemplateId,
    plannerElementSets,
    subjects: classSubjects,
    userType: getUserType(state),
    assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
  });

  const fieldList = _.get(updatedTemplate, "field_list", {});

  const defaultOptions = getOptionsFromField({
    fieldList,
    fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
    t,
  });

  const learningGoalsDropdownOptions = getAddLearningGoalsDropdownOptions({
    options: defaultOptions,
    resourceFields,
    unitPlanTemplate,
    subjects: classSubjects,
    fieldList,
  });

  const learningGoalsFields = getLearningGoalsFields(updatedTemplate);

  const nextQuestionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.nextQuestionResourceId",
    null
  );

  return {
    activeQuestionId,
    isGenWithAIEnabled,
    isGeneratingAIQuestions,
    learningGoalsFields,
    selectedQuestionFields,
    showWorksheetAssistant,
    questionAIConversationId,
    learningGoalsDropdownOptions,
    nextQuestionResourceId,
    hasAssessmentTools,
    hasInstruction,
    isAIAssessmentToolsOpen,
  };
};

const mapDispatchToProps = {
  updateLoadingState,
  createAIConversation,
  updateVisibleQuestionAssistantIds,
  updateWorksheetSidebar,
  updateAddQuestionsInBetween,
  updateAIAssessmentTools,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SinglePageQuestionEditorV2);
